import React, { useEffect, useState } from 'react';
import { DatePicker, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { Link } from 'react-router-dom';
import { Download, Inbox } from 'lucide-react';
import {
  getTopUpReport,
  TopUpReportItem,
} from '../../../../api/payment/withdraw-request.api';
import {
  AdminButton,
  AdminCard,
  AdminCardFooter,
  AdminPageHeader,
  AdminStatusPill,
} from '../../ui';

const { RangePicker } = DatePicker;

const fmtVnd = (n?: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;
const fmtCa = (n?: number) => `${Number(n || 0).toLocaleString('vi-VN')} Ca`;
const fmtDate = (s?: string) =>
  s ? dayjs.utc(s).local().format('DD/MM/YYYY HH:mm') : '';

const PAGE_SIZE = 20;

const ManagementTopUpReport: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<TopUpReportItem[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // Mặc định: tháng hiện tại
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().startOf('month'),
    dayjs().endOf('day'),
  ]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const from = range?.[0]?.startOf('day').toISOString();
      const to = range?.[1]?.endOf('day').toISOString();
      const data = await getTopUpReport(from, to, pageNumber, PAGE_SIZE);
      setItems(data.items || []);
      setTotalItems(data.totalItems || 0);
      setTotalPages(data.totalPages || 1);
    } catch (e) {
      toast.error('Không tải được nhật ký nạp số dư');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, pageNumber]);

  // Reset về trang 1 khi đổi kỳ
  const onRangeChange = (r: any) => {
    setPageNumber(1);
    setRange(r);
  };

  // Tổng của TRANG hiện tại (tổng cả kỳ xem ở "Báo cáo doanh thu")
  const pageSum = items.reduce(
    (acc, x) => {
      acc.amountVnd += x.amountVnd || 0;
      acc.caAmount += x.caAmount || 0;
      acc.gatewayFee += x.gatewayFee || 0;
      return acc;
    },
    { amountVnd: 0, caAmount: 0, gatewayFee: 0 }
  );

  const exportExcel = () => {
    if (!items.length) {
      toast.error('Không có dữ liệu để xuất');
      return;
    }
    const rows = items.map((x) => ({
      'Thời gian': fmtDate(x.createdAt),
      'Mã GD nội bộ (TxnRef)': x.ourTxnRef,
      'Mã GD VNPay': x.vnpayTransactionNo,
      'User ID': x.userId || '',
      'Số tiền nạp (VND)': x.amountVnd,
      'Ca cộng': x.caAmount,
      'Phí cổng VNPay (VND)': x.gatewayFee,
      'Trạng thái': x.status,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Nap Ca');
    const from = range?.[0]?.format('YYYYMMDD') || '';
    const to = range?.[1]?.format('YYYYMMDD') || '';
    XLSX.writeFile(wb, `so-nap-ca_${from}-${to}.xlsx`);
  };

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Sổ nạp số dư (đối soát VNPay)"
        description={
          <>
            Nhật ký các lệnh nạp thành công. Đối soát <b>Mã GD VNPay</b> với sao
            kê ngân hàng/VNPay. Tổng cả kỳ xem ở trang{' '}
            <Link className="adm-link" to="/admin/donate/revenue">
              Báo cáo doanh thu
            </Link>
            .
          </>
        }
        actions={
          <div className="flex items-center gap-3">
            <RangePicker
              value={range}
              onChange={onRangeChange}
              format="DD/MM/YYYY"
              allowClear={false}
            />
            <AdminButton
              elevated
              icon={<Download size={18} />}
              onClick={exportExcel}
            >
              Xuất Excel
            </AdminButton>
          </div>
        }
      />

      <AdminCard flush>
        <Spin spinning={loading}>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Thời gian</th>
                  <th>Mã GD nội bộ</th>
                  <th>Mã GD VNPay</th>
                  <th>User</th>
                  <th className="adm-num">Số tiền nạp</th>
                  <th className="adm-num">Ca cộng</th>
                  <th className="adm-num">Phí cổng</th>
                  <th style={{ textAlign: 'center' }}>Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {items.length ? (
                  items.map((x, i) => (
                    <tr key={`${x.ourTxnRef}-${i}`}>
                      <td className="whitespace-nowrap">
                        {fmtDate(x.createdAt)}
                      </td>
                      <td className="font-mono text-xs">{x.ourTxnRef}</td>
                      <td className="font-mono text-xs">
                        {x.vnpayTransactionNo || '—'}
                      </td>
                      <td className="font-mono text-xs" title={x.userId || ''}>
                        {x.userId ? `${x.userId.slice(0, 8)}…` : '—'}
                      </td>
                      <td className="adm-num font-semibold">
                        {fmtVnd(x.amountVnd)}
                      </td>
                      <td className="adm-num">{fmtCa(x.caAmount)}</td>
                      <td
                        className="adm-num"
                        style={{ color: 'var(--adm-warning)' }}
                      >
                        {fmtVnd(x.gatewayFee)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <AdminStatusPill tone="success">
                          {x.status}
                        </AdminStatusPill>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      style={{ height: 160, textAlign: 'center' }}
                      className="adm-muted"
                    >
                      <div className="flex flex-col items-center gap-3">
                        <span
                          className="inline-flex items-center justify-center"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 12,
                            background: 'var(--adm-fill)',
                          }}
                        >
                          <Inbox size={20} />
                        </span>
                        Không có giao dịch nạp trong kỳ
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              {items.length > 0 && (
                <tfoot>
                  <tr
                    style={{
                      background: 'var(--adm-bg)',
                      fontWeight: 600,
                      color: 'var(--adm-heading)',
                    }}
                  >
                    <td style={{ padding: '14px 20px' }} colSpan={4}>
                      Tổng trang {pageNumber} ({items.length} dòng)
                    </td>
                    <td className="adm-num" style={{ padding: '14px 16px' }}>
                      {fmtVnd(pageSum.amountVnd)}
                    </td>
                    <td className="adm-num" style={{ padding: '14px 16px' }}>
                      {fmtCa(pageSum.caAmount)}
                    </td>
                    <td
                      className="adm-num"
                      style={{
                        padding: '14px 16px',
                        color: 'var(--adm-warning)',
                      }}
                    >
                      {fmtVnd(pageSum.gatewayFee)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Spin>

        <AdminCardFooter>
          <div className="flex w-full items-center justify-between gap-4 text-sm adm-muted">
            <span>Tổng {totalItems.toLocaleString('vi-VN')} giao dịch</span>
            <div className="adm-pagination">
              <button
                className="adm-page-btn adm-page-btn--nav"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                Trước
              </button>
              <span className="px-2" style={{ color: 'var(--adm-heading)' }}>
                {pageNumber} / {totalPages || 1}
              </span>
              <button
                className="adm-page-btn adm-page-btn--nav"
                disabled={pageNumber >= totalPages}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                Sau
              </button>
            </div>
          </div>
        </AdminCardFooter>
      </AdminCard>
    </div>
  );
};

export default ManagementTopUpReport;
